import { z } from "zod";

/**
 * Generic report-canvas serialization: the persisted form of a canvas layout,
 * shared by every consumer of the canvas framework.
 *
 * The serialized shape follows the query-builder saved-report format
 * (`./queryReport.ts`): `data[row][cell]` is one component config — position
 * is the identity of a cell, there are no row/cell wrapper objects, and
 * runtime drag-and-drop ids are regenerated on load. The difference is the
 * component schema: this module's is open (`passthrough`) so surfaces can
 * register their own widget types without touching shared code, while the
 * query-builder narrows to its concrete chart union before persisting.
 */

/**
 * Layout size caps, shared by the serialized schema and the canvas UI so
 * drops that would exceed them are rejected at interaction time instead of
 * failing at save time.
 */
export const REPORT_CANVAS_MAX_ROWS = 12;
export const REPORT_CANVAS_MAX_CELLS_PER_ROW = 6;

/**
 * Identity + plumbing fields every canvas component config shares. Concrete
 * chart schemas (see `./queryReport.ts`) extend this so a component validated
 * by either schema round-trips through the other.
 */
export const CanvasComponentBaseSchema = z.object({
  id: z.string().min(1),
  /**
   * Named dataset this component reads from the surface's data context.
   * Undefined = the surface's default dataset (single-dataset surfaces like
   * the query builder never set this).
   */
  datasetKey: z.string().optional(),
  /**
   * Config schema version for this component type. Absent = 1. Component
   * config migrations run client-side against the canvas registry on read.
   */
  v: z.number().int().min(1).optional(),
});

/** Open component config: widget-specific fields flow through untyped by design. */
export const CanvasComponentConfigSchema = CanvasComponentBaseSchema.extend({
  type: z.string().min(1),
}).passthrough();
export type CanvasComponentConfig = z.infer<typeof CanvasComponentConfigSchema>;

/** Serialized canvas layout: rows of component configs, capped. */
export const ReportCanvasLayoutSchema = z.object({
  data: z
    .array(
      z
        .array(CanvasComponentConfigSchema)
        .min(1)
        .max(REPORT_CANVAS_MAX_CELLS_PER_ROW),
    )
    .max(REPORT_CANVAS_MAX_ROWS),
});
export type ReportCanvasLayout = z.infer<typeof ReportCanvasLayoutSchema>;

/**
 * The stored JSONB envelope for a surface layout row in `core_saved_reports`
 * (a per-user override or org default keyed by `surface_id`). Same envelope
 * shape as the query-builder's saved-report document (`./queryReport.ts`)
 * minus `query` — surface layouts chart page-provided datasets, not a query.
 */
export const CanvasSavedReportDocumentSchema = z.object({
  schemaVersion: z.number().int(),
  report: ReportCanvasLayoutSchema,
});
export type CanvasSavedReportDocument = z.infer<
  typeof CanvasSavedReportDocumentSchema
>;
